import { differenceInMinutes } from 'date-fns'
import type { AbstractStorage } from './storage'

export type CachedContainer<T> = { cachedAt: Nullable<string | Date>, value: T }

export const memory = <T>() => new Map<string | number, CachedContainer<T>>() // In memory store

const defaultStore = memory()

type Storage<T> = Map<string | number, CachedContainer<T>> | AbstractStorage

/**
 * Smart cache hook (resets on page reload)
 * @param key - cache key
 * @param timeout - time in minutes
 * @param initialValue
 * @param onExpire - callback to renew cache on expire
 * @param store - your custom store (new Map)
 * @returns
 */
export function useCache<T>(key: string | number, timeout: number, initialValue: Nullable<T> = null, onExpire?: () => Promise<T>, store: Storage<T> = defaultStore as Storage<T>, ignoreUndefined = false) {
  const initial = {
    cachedAt: null,
    value: initialValue
  } as CachedContainer<T>

  if (timeout <= 0) ignoreUndefined = true

  const isUndef = ignoreUndefined && initialValue == null

  if (!store) store = defaultStore as Storage<T>
  if (!store.has(key) && !isUndef) store.set(key, initial)

  const isExpiried = () => {
    if (timeout <= 0) return true
    else if (Number.isNaN(timeout)) return !!store.get(key)?.value

    const snapshot = store.get(key) || initial
    const diff = differenceInMinutes(new Date, new Date(snapshot.cachedAt as Date))
    const isEmptyArray = (store.has(key) && Array.isArray(store.get(key)?.value) && !(<[]>store.get(key)?.value).length)

    return !store.has(key) || (+diff >= +timeout) || !store.get(key)?.value || isEmptyArray
  }

  const cache = (value: T) => {
    if (ignoreUndefined && value == null || timeout <= 0) return value

    store.set(key, {
      cachedAt: new Date,
      value
    })

    return value
  }

  const readAndCache = async (onExpireFn?: () => Promise<T>): Promise<T> => {
    if (onExpireFn) onExpire = onExpireFn
    if (store.has(key) && !isExpiried()) return (store.get(key)?.value || initialValue) as T
    else if (onExpire && (typeof onExpire === 'function' || (<unknown>onExpire instanceof Promise))) {
      const payload = await Promise.resolve(onExpire())
      if (payload !== undefined && payload !== null) return cache(payload)
    }

    return initialValue as T
  }

  const read = (): T => {
    if (!isExpiried() && store.has(key)) return (store.get(key)?.value || initialValue) as T
    return initialValue as T
  }


  const clear = () => store.delete(key)
  const reset = () => store.clear()

  return {
    isExpiried,
    reset,
    store,
    cache,
    clear,
    read,
    readAndCache
  }
}
